import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import { logger } from "../lib/logger";
import Seo, { breadcrumbLd } from "../components/Seo";
import { ArrowLeft, Palette } from "lucide-react";
import { ART_CATEGORIES, categoryBySlug } from "../lib/categories";

const resolveImg = (url) =>
  url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;

export default function Category() {
  const { slug } = useParams();
  const category = categoryBySlug(slug);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!category) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/artworks", {
          params: { medium: category.medium },
        });
        if (active) setArtworks(data);
      } catch (err) {
        logger.error("Category list load failed:", err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [category]);

  if (!category) return <Navigate to="/gallery" replace />;

  return (
    <div data-testid="category-page" className="bg-[#FAF6EE] min-h-screen">
      <Seo
        title={category.seoTitle || `${category.label} — Original African Art`}
        description={
          category.seoDescription ||
          `Browse original ${category.label.toLowerCase()} from contemporary African artists on AfricaArtMasters. Direct from the studio.`
        }
        path={`/category/${category.slug}`}
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Gallery", path: "/gallery" },
          { name: category.label, path: `/category/${category.slug}` },
        ])}
      />

      {/* --- Editorial hero ---------------------------------------- */}
      <section className="relative overflow-hidden bg-[#2A180F] text-[#F4EFE6]">
        <div className="absolute inset-0">
          <div
            className="w-full h-full"
            style={{
              background:
                "radial-gradient(circle at 20% 30%, #C97B3B 0%, transparent 55%), radial-gradient(circle at 80% 70%, #7A3D1F 0%, transparent 60%), linear-gradient(180deg, #3A2418 0%, #2A180F 100%)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2A180F] via-[#2A180F]/60 to-[#2A180F]/30" />
        </div>

        <div className="relative px-6 md:px-12 lg:px-24 pt-10 pb-16 md:pb-24 min-h-[46vh] flex flex-col justify-end">
          <Link
            to="/gallery"
            data-testid="category-back-link"
            className="label-eyebrow text-[#F4C430] hover:text-white transition-colors flex items-center gap-2 mb-auto"
          >
            <ArrowLeft className="w-3 h-3" />
            All works
          </Link>

          <div className="mt-10 max-w-5xl">
            <p className="label-eyebrow text-[#F4C430] tracking-[0.3em]">
              — The Collection
            </p>
            <h1
              data-testid="category-title"
              className="font-serif-display text-6xl md:text-8xl lg:text-9xl tracking-tighter leading-[0.88] font-semibold mt-6"
            >
              {category.label}
            </h1>
            <div className="mt-8 flex items-center gap-2 label-eyebrow text-[#F4C430]">
              <Palette className="w-4 h-4" strokeWidth={1.5} />
              {artworks.length} work{artworks.length === 1 ? "" : "s"} on view
            </div>
          </div>
        </div>
      </section>

      {/* --- Works grid -------------------------------------------- */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
        {loading ? (
          <p className="label-eyebrow text-[#7A5C3D]">Loading {category.label.toLowerCase()}…</p>
        ) : artworks.length > 0 ? (
          <div
            data-testid="category-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12"
          >
            {artworks.map((art) => (
              <CategoryArtworkCard key={art.id} art={art} />
            ))}
          </div>
        ) : (
          <div className="py-10">
            <p className="font-serif-display text-3xl md:text-4xl text-[#7A5C3D] font-light">
              No {category.label.toLowerCase()} on view yet.
            </p>
            <Link
              to="/gallery"
              className="mt-6 inline-block label-eyebrow text-[#1E3A8A] hover:text-[#111] transition-colors"
            >
              Explore the full gallery →
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}

/* Gallery-quality artwork card — mirrors the Art Hub card layout. */
function CategoryArtworkCard({ art }) {
  const img = resolveImg(art.image_url);
  const price = art.price
    ? new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: art.currency || "USD",
        maximumFractionDigits: 0,
      }).format(art.price)
    : null;
  return (
    <Link
      to={`/artwork/${art.id}`}
      data-testid={`category-artwork-${art.id}`}
      className="group block"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#E8DFCA]">
        <img
          src={img}
          alt={art.title}
          className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A180F]/85 via-[#2A180F]/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
        {price && (
          <p className="absolute top-4 right-4 font-mono-aam text-[11px] tracking-widest bg-[#F4C430] text-[#2A180F] px-2.5 py-1">
            {price}
          </p>
        )}
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
          <p className="label-eyebrow text-[#F4C430]">{art.medium || "Original"}</p>
          <h3 className="font-serif-display text-2xl md:text-3xl mt-2 leading-tight text-[#F4EFE6]">
            {art.title}
          </h3>
          <div className="mt-3 h-px w-10 bg-[#F4C430] transition-all duration-500 group-hover:w-24" />
          <p className="mt-3 text-sm text-[#E8DFCA] font-light">
            {art.artist_name}
            {art.dimensions ? (
              <span className="ml-2 text-[#E8DFCA]/60">· {art.dimensions}</span>
            ) : null}
          </p>
        </div>
      </div>
    </Link>
  );
}
